/* Shared local-preview and Shopify behavior. No store credentials or analytics. */
(function (scope) {
  'use strict';
  function positiveInt(value) { return Number.isSafeInteger(value) && value > 0; }
  function buildItems(ids, variants) {
    const merged = new Map();
    for (const id of ids) {
      const variant = variants.find(v => String(v.id) === String(id));
      if (!variant || variant.available !== true) throw new Error('Choose an available option for every item.');
      merged.set(String(id), {id: variant.id, quantity: (merged.get(String(id))?.quantity || 0) + 1});
    }
    return [...merged.values()];
  }
  function quote(lines, productId, tiers, shipping) {
    let subtotal = 0, eligible = 0, quantity = 0;
    for (const line of lines) {
      if (!positiveInt(line.quantity) || !Number.isSafeInteger(line.price) || line.price < 0) throw new Error('Invalid cart line.');
      subtotal += line.price * line.quantity;
      if (String(line.productId) === String(productId)) { eligible += line.price * line.quantity; quantity += line.quantity; }
    }
    const discount = Math.min(eligible, Math.max(0, ...tiers.filter(t => quantity >= t.quantity).map(t => t.discount_cents)));
    if (!Number.isSafeInteger(discount) || discount < 0) throw new Error('Invalid discount.');
    const merchandise = subtotal - discount;
    const threshold = shipping.free_threshold_cents;
    const delivery = threshold != null && merchandise >= threshold ? 0 : shipping.flat_rate_cents;
    return {quantity, subtotal, discount, merchandise, shipping:delivery, total:delivery == null ? null : merchandise + delivery};
  }
  function selectionItems(quantity, selected, variants, config) {
    if (!config.packetSets) return buildItems(selected, variants);
    const tier=config.tiers.find(t=>t.quantity===quantity);
    const variant=tier&&variants.find(v=>String(v.id)===String(tier.variant_id));
    if(!variant || !variant.available || variant.price!==config.unitPrice*quantity-tier.discount_cents) throw new Error('This packet set is being updated. Please choose another option.');
    return [{id:variant.id,quantity:1}];
  }
  function dispatchWindow(now, settings) {
    const fmt=new Intl.DateTimeFormat('en-US',{timeZone:settings.time_zone,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
    const parts=dt=>Object.fromEntries(fmt.formatToParts(dt).filter(p=>p.type!=='literal').map(p=>[p.type,Number(p.value)]));
    const p=parts(now), today=new Date(Date.UTC(p.year,p.month-1,p.day));
    const business=d=>settings.calendar_days===true||(d.getUTCDay()!==0&&d.getUTCDay()!==6);
    const nextDay=d=>new Date(d.getTime()+86400000);
    let orderDay=today;
    if(p.hour*60+p.minute>=settings.hour*60+settings.minute)orderDay=nextDay(orderDay);
    while(!business(orderDay))orderDay=nextDay(orderDay);
    // Resolve the deadline in its own time zone; this also handles DST weekends.
    const wall=Date.UTC(orderDay.getUTCFullYear(),orderDay.getUTCMonth(),orderDay.getUTCDate(),settings.hour,settings.minute);
    let deadline=wall;
    for(let i=0;i<3;i++){const z=parts(new Date(deadline));deadline+=wall-Date.UTC(z.year,z.month-1,z.day,z.hour,z.minute,z.second);}
    const addBusiness=count=>{let d=new Date(orderDay);while(count>0){d=nextDay(d);if(business(d))count--;}return d;};
    const label=d=>new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',timeZone:'UTC'}).format(d);
    return {openToday:business(today)&&orderDay.getTime()===today.getTime(),deadline:new Date(deadline).toISOString(),remainingSeconds:Math.max(0,Math.ceil((deadline-now.getTime())/1000)),dispatchStart:label(addBusiness(settings.min_days)),dispatchEnd:label(addBusiness(settings.max_days)),cutoffDay:new Intl.DateTimeFormat('en-US',{weekday:'short',month:'short',day:'numeric',timeZone:'UTC'}).format(orderDay)};
  }
  function offerRemaining(now, deadline) {
    const end=Date.parse(deadline);
    return Number.isFinite(end)&&end>now.getTime()?Math.ceil((end-now.getTime())/1000):null;
  }
  function recurringOfferRemaining(now, cycle) {
    const start=Date.parse(cycle.anchor), seconds=cycle.seconds;
    if(!Number.isFinite(start)||!Number.isSafeInteger(seconds)||seconds<=0) return null;
    const elapsed=(now.getTime()-start)/1000;
    // A shared absolute anchor keeps reloads, languages and DST on one schedule.
    const phase=((elapsed%seconds)+seconds)%seconds;
    return Math.ceil(seconds-phase);
  }
  const api = {buildItems, quote, selectionItems, dispatchWindow, offerRemaining, recurringOfferRemaining};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  scope.StoreKit = api;
  if (typeof document === 'undefined') return;
  function boot(root) {
    if (root.dataset.scReady) return;
    const configEl = root.querySelector('[data-sc-config]');
    if (!configEl) return;
    const cfg = JSON.parse(configEl.textContent);
    const tr = value => cfg.messages?.[value] || value;
    root.dataset.scReady = 'true';
    let qty = cfg.defaultQuantity, selected = [], previewCart = [], busy = false;
    const variants = cfg.variants;
    const money = cents => new Intl.NumberFormat(cfg.locale || 'en-US', {style:'currency',currency:cfg.currency}).format(cents / 100);
    const q = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];
    if((cfg.offerGuarantee||cfg.dispatch) && q('[data-sc-dispatch]')) {
      const tick=()=>{
        if(!root.isConnected){clearInterval(timer);return;}
        let seconds;
        if(cfg.offerGuarantee){
          seconds=cfg.offerGuarantee.recurring_cycle?recurringOfferRemaining(new Date(),cfg.offerGuarantee.recurring_cycle):(cfg.offerGuarantee.daily_reset?dispatchWindow(new Date(),{...cfg.offerGuarantee.daily_reset,calendar_days:true,min_days:0,max_days:0}).remainingSeconds:offerRemaining(new Date(),cfg.offerGuarantee.ends_at));
          q('[data-sc-dispatch]').hidden=seconds===null;
          if(seconds===null){clearInterval(timer);return;}
        }else{const state=dispatchWindow(new Date(),cfg.dispatch);seconds=state.openToday?state.remainingSeconds:0;}
        q('[data-sc-countdown]').textContent=[Math.floor(seconds/3600),Math.floor(seconds%3600/60),seconds%60].map(n=>String(n).padStart(2,'0')).join(':');
      };
      const timer=setInterval(tick,1000);tick();
    }
    const first = variants.find(v => v.available);
    const dialog = q('[data-sc-dialog]');
    const compatible = cfg.currency === cfg.expectedCurrency && (cfg.packetSets ? cfg.tiers.every(t=>variants.some(v=>String(v.id)===String(t.variant_id)&&v.price===cfg.unitPrice*t.quantity-t.discount_cents)) : variants.filter(v => v.available).every(v => v.price === cfg.unitPrice));
    function report(text) { q('[data-sc-status]').textContent = tr(text); }
    function renderPickers() {
      selected = Array.from({length:qty}, (_, i) => selected[i] || first?.id || '');
      q('[data-sc-pickers]').replaceChildren();
      if (cfg.packetSets) return;
      if (variants.length < 2) return;
      selected.forEach((id,i) => {
        const label = document.createElement('label'); label.className = 'sc-unit-picker';
        label.append(document.createTextNode(cfg.unitSingular + ' ' + (i + 1)));
        const select = document.createElement('select'); select.setAttribute('aria-label', 'Option for ' + cfg.unitSingular + ' ' + (i + 1));
        for (const v of variants) { const option = document.createElement('option');option.value=String(v.id);option.textContent=v.title+(v.available?'':' — unavailable');option.disabled=!v.available;select.append(option); }
        select.value=String(id);select.addEventListener('change',()=>{selected[i]=select.value;sync();});label.append(select);q('[data-sc-pickers]').append(label);
      });
    }
    function selectionLines() {
      return selectionItems(qty,selected,variants,cfg).map(item => ({...item, price:variants.find(v=>String(v.id)===String(item.id)).price, productId:cfg.productId}));
    }
    function sync() {
      all('[data-sc-tier]').forEach(b=>{b.setAttribute('aria-pressed',String(Number(b.dataset.scTier)===qty));});
      let price=cfg.checkoutEnabled ? null : quote([{quantity:qty,price:cfg.unitPrice,productId:cfg.productId}],cfg.productId,cfg.tiers,cfg.shipping).merchandise;
      try { price = quote(selectionLines(),cfg.productId,cfg.packetSets?[]:cfg.tiers,cfg.shipping).merchandise; } catch (_) {}
      all('[data-sc-selection-price]').forEach(el=>{el.textContent=price===null?tr('Unavailable'):money(price);});
      all('[data-sc-add]').forEach(b=>{b.disabled=busy || price===null || (!cfg.preview && (!cfg.checkoutEnabled || !compatible));});
      const shop=q('[data-sc-shop-pay]');
      if(shop){
        let items=[];try{items=selectionItems(qty,selected,variants,cfg);}catch(_){}
        const allowed=cfg.checkoutEnabled&&compatible&&price!==null&&items.length>0;
        shop.hidden=!allowed;
        if(allowed)shop.querySelector('shop-pay-button').setAttribute('variants',items.map(i=>`${i.id}:${i.quantity}`).join(','));
      }
      if (!cfg.preview && !compatible) report('Ordering is paused while pricing is updated.');
    }
    function showCart() {
      const list=q('[data-sc-cart-lines]');list.replaceChildren();
      if (!previewCart.length) {const p=document.createElement('p');p.textContent=tr('Your preview selection is empty.');list.append(p);}
      for (const line of previewCart) {
        const row=document.createElement('div');row.className='sc-line';const label=document.createElement('span');label.textContent=`${line.quantity} × ${cfg.title}`+(cfg.packetSets?' — '+variants.find(v=>String(v.id)===String(line.id)).title:'');
        const val=document.createElement('strong');val.textContent=money(line.quantity*line.price);row.append(label,val);list.append(row);
      }
      const total=quote(previewCart,cfg.productId,cfg.packetSets?[]:cfg.tiers,cfg.shipping);
      q('[data-sc-cart-total]').textContent=money(total.merchandise);
      q('[data-sc-cart-count]').textContent=String(previewCart.reduce((n,l)=>n+l.quantity,0));
      if(!dialog.open) dialog.showModal();
    }
    async function add() {
      if (busy) return;
      let items;
      try {items=selectionItems(qty,selected,variants,cfg);}catch(e){report(e.message);return;}
      if (cfg.preview) {
        for(const line of selectionLines()){const old=previewCart.find(l=>String(l.id)===String(line.id));if(old)old.quantity+=line.quantity;else previewCart.push({...line});}
        showCart();report('Added to your local preview selection. No order was placed.');return;
      }
      if(!cfg.checkoutEnabled || !compatible) {report('Ordering is not open yet.');return;}
      busy=true;sync();report('Adding to your cart…');
      try {
        const response=await fetch(cfg.cartAdd, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const data=await response.json();
        if(!response.ok)throw new Error(data.description || data.message || 'Could not add these items. Please try again.');
        // Shopify owns final discounts, shipping and totals. This never clears the cart.
        window.location.assign(cfg.cartUrl);
      }catch(e){report(e.message || 'Connection failed. Please try again.');}finally{busy=false;sync();}
    }
    all('[data-sc-tier]').forEach(b=>b.addEventListener('click',()=>{qty=Number(b.dataset.scTier);renderPickers();sync();}));
    all('[data-sc-add]').forEach(b=>b.addEventListener('click',add));
    all('[data-sc-open-cart]').forEach(b=>b.addEventListener('click',()=>{if(cfg.preview)showCart();else window.location.assign(cfg.cartUrl);}));
    all('[data-sc-gallery]').forEach(b=>b.addEventListener('click',()=>{
      q('[data-sc-main-image]').src=b.dataset.scSrc;q('[data-sc-main-image]').alt=b.dataset.scAlt;
      all('[data-sc-gallery]').forEach(t=>t.setAttribute('aria-pressed',String(t===b)));
    }));
    const lightbox=q('[data-sc-lightbox]');
    q('[data-sc-zoom]')?.addEventListener('click',()=>{const img=q('[data-sc-main-image]');q('[data-sc-lightbox-image]').src=img.src;q('[data-sc-lightbox-image]').alt=img.alt;lightbox.showModal();});
    q('[data-sc-lightbox-close]')?.addEventListener('click',()=>lightbox.close());
    all('a[href="#sc-write-review"]').forEach(a=>a.addEventListener('click',()=>{const form=q('#sc-write-review');if(form)form.open=true;}));
    lightbox?.addEventListener('click',e=>{if(e.target===lightbox)lightbox.close();});
    all('[data-sc-carousel-group]').forEach(group=>{
      const carousel=group.querySelector('[data-sc-carousel]');
      if(!carousel)return;
      const buttons=[...group.querySelectorAll('[data-sc-slide]')];
      const update=()=>buttons.forEach(b=>{b.disabled=Number(b.dataset.scSlide)<0?carousel.scrollLeft<=1:carousel.scrollLeft+carousel.clientWidth>=carousel.scrollWidth-2;});
      buttons.forEach(b=>b.addEventListener('click',()=>{
        const card=carousel.querySelector('article');if(!card)return;
        const gap=parseFloat(getComputedStyle(carousel).columnGap)||0;
        carousel.scrollBy({left:Number(b.dataset.scSlide)*(card.getBoundingClientRect().width+gap),behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      }));
      carousel.addEventListener('scroll',update,{passive:true});
      if(typeof ResizeObserver!=='undefined')new ResizeObserver(update).observe(carousel);
      update();
    });
    q('[data-sc-close]').addEventListener('click',()=>dialog.close());
    q('[data-sc-clear]').addEventListener('click',()=>{previewCart=[];showCart();});
    dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
    renderPickers();sync();
  }
  const run = () => document.querySelectorAll('[data-sc-root]').forEach(boot);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run);else run();
  if(!scope.__storeKitSectionHook){document.addEventListener('shopify:section:load',run);scope.__storeKitSectionHook=true;}
})(typeof globalThis !== 'undefined' ? globalThis : this);
